// ─── Enums ─────────────────────────────────────────────────────────────────
// Using regular objects instead of const enums (erasableSyntaxOnly compatible)

export const UserRole = {
  SuperAdmin: 0,
  Admin: 1,
  /** Company owner — can manage company profile, post jobs, review applicants */
  EmployerAdmin: 2,
  JobSeeker: 3,
  Student: 4,
  /** Employer staff — can post jobs and review applicants, but not edit company profile */
  Employer: 5,
} as const;
export type UserRole = (typeof UserRole)[keyof typeof UserRole];

export const JobStatus = {
  Draft: 'Draft',
  Active: 'Active',
  Paused: 'Paused',
  Closed: 'Closed',
  Expired: 'Expired',
} as const;
export type JobStatus = (typeof JobStatus)[keyof typeof JobStatus];

export const ApplicationStatus = {
  Pending: 'Pending',
  Reviewing: 'Reviewing',
  Shortlisted: 'Shortlisted',
  Interviewing: 'Interviewing',
  Offered: 'Offered',
  Rejected: 'Rejected',
  Withdrawn: 'Withdrawn',
  /** Employer directly invited a candidate who had not applied */
  Invited: 'Invited',
  /** Candidate declined the employer's direct invite */
  InviteDeclined: 'InviteDeclined',
} as const;
export type ApplicationStatus = (typeof ApplicationStatus)[keyof typeof ApplicationStatus];

export const ExperienceLevel = {
  Internship: 'Internship',
  EntryLevel: 'EntryLevel',
  MidLevel: 'MidLevel',
  SeniorLevel: 'SeniorLevel',
  Lead: 'Lead',
  Executive: 'Executive',
} as const;
export type ExperienceLevel = (typeof ExperienceLevel)[keyof typeof ExperienceLevel];

export const JobType = {
  FullTime: 'FullTime',
  PartTime: 'PartTime',
  Contract: 'Contract',
  Freelance: 'Freelance',
  Internship: 'Internship',
  Temporary: 'Temporary',
} as const;
export type JobType = (typeof JobType)[keyof typeof JobType];

// ─── Core Interfaces ───────────────────────────────────────────────────────

export interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: UserRole;
  roleName?: string;
  isActive: boolean;
  phoneNumber?: string;
  avatarUrl?: string;
  bio?: string;
  linkedInUrl?: string;
  portfolioUrl?: string;
  resumeUrl?: string;
  resumeFileName?: string;
  createdAt: string;
  updatedAt?: string;
  skills?: string;        // comma-separated skill keywords
  isPremiumUser?: boolean;
  isEmailVerified?: boolean;
  isPhoneVerified?: boolean;
  isApproved?: boolean;       // false for Employer until SuperAdmin approves
  companyId?: string;         // set for Employer staff (not EmployerAdmin)
  introVideoUrl?: string;
  preferredLocations?: string; // JSON string of LocationValue[]
}

export interface Company {
  id: string;
  name: string;
  description?: string;
  logoUrl?: string;
  website?: string;
  industry?: string;
  size?: string;
  location?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface Country {
  id: string;
  name: string;
  code: string;
}

export interface State {
  id: string;
  name: string;
  countryId: string;
}

export interface City {
  id: string;
  name: string;
  stateId: string;
}

export interface Job {
  id: string;
  title: string;
  slug?: string;
  shortCode?: string;  // opaque code for shareable short links (/j/{shortCode})
  description: string;
  requirements?: string;
  benefits?: string;
  location: string;
  isRemote: boolean;
  jobType: JobType;
  experienceLevel: ExperienceLevel;
  minExperienceYears?: number;  // minimum years of experience required
  maxExperienceYears?: number;  // maximum years of experience accepted (null = no upper bound)
  salaryMin?: number;
  salaryMax?: number;
  currency: string;
  tags?: string;    // comma-separated tag keywords
  skills?: string;  // comma-separated required skill keywords
  status: JobStatus;
  deadline?: string;
  companyId: string;
  companyName?: string;
  companyLogoUrl?: string;
  postedById?: string;       // user ID of the employer who posted the job
  company?: Company;
  employerId?: string;
  employer?: User;
  applicationCount?: number;
  createdAt: string;
  updatedAt?: string;
  isPremium?: boolean;
  // Location hierarchy (IDs + names)
  cityId?: string;
  cityName?: string;
  stateId?: string;
  stateName?: string;
  countryId?: string;
  countryName?: string;
  /** External provider key when imported (e.g. JobDataLake). Null = posted on portal. */
  importSource?: string | null;
  externalJobId?: string | null;
}

export interface JobApplication {
  id: string;
  jobId: string;
  jobTitle?: string;
  companyName?: string;
  job?: Job;
  applicantId: string;
  applicantName?: string;
  applicantEmail?: string;
  applicant?: User;
  status: ApplicationStatus;
  coverLetter?: string;
  resumeUrl?: string;
  resumeFileName?: string;
  interviewDate?: string;
  interviewType?: string;
  interviewLink?: string;
  offerAmount?: number;
  notes?: string;
  appliedAt?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface SystemSetting {
  id: string;
  category: string;
  key: string;
  value: string;
  isEncrypted: boolean;
  description?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface Notification {
  id: string;
  userId: string;
  title: string;
  message: string;
  isRead: boolean;
  type: string;
  referenceId?: string;
  createdAt: string;
}

// ─── Pagination & API ──────────────────────────────────────────────────────

export interface PaginatedList<T> {
  items: T[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

export interface ApiResponse<T = void> {
  success: boolean;
  data?: T;
  message?: string;
  errors?: string[];
}

// ─── Auth ──────────────────────────────────────────────────────────────────

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  role: UserRole;
}

export interface TokenResponse {
  token: string;
  refreshToken?: string;
  expiresAt: string;
  user: User;
}

export interface Right {
  id: string;
  name: string;
  category: string;
  description?: string;
  isActive: boolean;
}

// ─── Job Filters ───────────────────────────────────────────────────────────

export interface JobFilters {
  keyword?: string;
  location?: string;
  jobType?: JobType;
  experienceLevel?: ExperienceLevel;
  salaryMin?: number;
  salaryMax?: number;
  companyId?: string;
  status?: JobStatus;
  pageNumber?: number;
  pageSize?: number;
  sortBy?: string;
  sortDescending?: boolean;
  // Structured location hierarchy
  cityId?: string;
  stateId?: string;
  countryId?: string;
  // Experience year range filters
  minExperienceYears?: number;
  maxExperienceYears?: number;
  isImported?: boolean;
  importSource?: string;
}
